import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';

// Fade In Up Animation
export const FadeInUp = ({ children, delay = 0, duration = 0.6, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Fade In Scale
export const FadeInScale = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Staggered Children Animation
export const StaggerContainer = ({ children, className = "", staggerDelay = 0.1 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        visible: {
          transition: {
            staggerChildren: staggerDelay
          }
        }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem = ({ children, className = "" }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y: 30 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
    }}
    className={`h-full ${className}`}
  >
    {children}
  </motion.div>
);

// Animated Counter
export const AnimatedCounter = ({ end, duration = 2, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    
    let startTime;
    const startValue = 0;
    const endValue = parseFloat(end.toString().replace(/[^0-9.]/g, ''));

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      
      // Easing function
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = startValue + (endValue - startValue) * easeOutQuart;
      
      setCount(Math.floor(currentValue));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return (
    <span ref={ref}>
      {prefix}{count}{suffix}
    </span>
  );
};

// Parallax Section
export const ParallaxSection = ({ children, className = "", speed = 0.5 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 100 * speed]);

  return (
    <motion.div ref={ref} style={{ y }} className={className}>
      {children}
    </motion.div>
  );
};

// Floating Animation
export const FloatingElement = ({ children, className = "", duration = 3, y = 10 }) => (
  <motion.div
    animate={{ y: [-y, y, -y] }}
    transition={{ duration, repeat: Infinity, ease: "easeInOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

// Pulse Animation
export const PulseElement = ({ children, className = "" }) => (
  <motion.div
    animate={{ scale: [1, 1.05, 1] }}
    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

// Scroll Indicator
export const ScrollIndicator = () => (
  <motion.div
    className="absolute bottom-8 left-1/2 -translate-x-1/2"
    animate={{ y: [0, 10, 0] }}
    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
  >
    <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
      <motion.div
        className="w-1.5 h-1.5 bg-white rounded-full"
        animate={{ y: [0, 12, 0], opacity: [1, 0, 1] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  </motion.div>
);

// Animated Background Gradient
export const AnimatedGradientBg = ({ className = "" }) => (
  <div className={`absolute inset-0 overflow-hidden ${className}`}>
    <motion.div
      className="absolute -inset-[100%] opacity-30"
      animate={{
        background: [
          "radial-gradient(circle at 20% 50%, #2563EB 0%, transparent 50%)",
          "radial-gradient(circle at 80% 50%, #2563EB 0%, transparent 50%)",
          "radial-gradient(circle at 50% 80%, #2563EB 0%, transparent 50%)",
          "radial-gradient(circle at 20% 50%, #2563EB 0%, transparent 50%)",
        ]
      }}
      transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
    />
  </div>
);

// Animated Blobs (optimized: smaller size, lighter blur, GPU-hinted, slower animation for less repaint pressure)
export const AnimatedBlobs = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <motion.div
      className="absolute top-1/4 -left-24 w-64 h-64 bg-[#2563EB]/15 rounded-full blur-2xl"
      style={{ willChange: 'transform' }}
      animate={{
        x: [0, 60, 0],
        y: [0, 30, 0],
      }}
      transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute bottom-1/4 -right-24 w-64 h-64 bg-[#0A2540]/20 rounded-full blur-2xl"
      style={{ willChange: 'transform' }}
      animate={{
        x: [0, -60, 0],
        y: [0, -30, 0],
      }}
      transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>
);

// Video Modal
export const VideoModal = ({ isOpen, onClose, videoUrl, title }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            aria-label="Close video"
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {videoUrl ? (
            <video
              className="w-full h-full"
              controls
              autoPlay
              playsInline
              preload="metadata"
              controlsList="nodownload"
            >
              <source src={videoUrl} />
              Your browser does not support the video tag.
            </video>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white">
              <div className="text-center">
                <div className="w-20 h-20 bg-[#2563EB] rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
                <p className="text-lg">{title}</p>
                <p className="text-white/60 text-sm mt-2">Video coming soon</p>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// Interactive Card
export const InteractiveCard = ({ children, className = "" }) => (
  <motion.div
    whileHover={{ y: -8, scale: 1.02 }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
    className={`h-full ${className}`}
  >
    {children}
  </motion.div>
);

// Wave Divider SVG
export const WaveDivider = ({ flip = false, color = "#F8FAFC" }) => (
  <div className={`w-full overflow-hidden leading-none ${flip ? 'rotate-180' : ''}`}>
    <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-16 md:h-24">
      <path
        d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
        fill={color}
      />
    </svg>
  </div>
);

// Curved Divider
export const CurvedDivider = ({ flip = false, color = "#F8FAFC" }) => (
  <div className={`w-full overflow-hidden leading-none ${flip ? 'rotate-180' : ''}`}>
    <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-12 md:h-20">
      <path
        d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
        fill={color}
      />
    </svg>
  </div>
);

// Angled Divider
export const AngledDivider = ({ flip = false, color = "#0A2540" }) => (
  <div className={`w-full overflow-hidden leading-none ${flip ? 'rotate-180' : ''}`}>
    <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-12 md:h-16">
      <path d="M1200 120L0 16.48 0 0 1200 0 1200 120z" fill={color} />
    </svg>
  </div>
);

export default {
  FadeInUp,
  FadeInScale,
  StaggerContainer,
  StaggerItem,
  AnimatedCounter,
  ParallaxSection,
  FloatingElement,
  PulseElement,
  ScrollIndicator,
  AnimatedGradientBg,
  AnimatedBlobs,
  VideoModal,
  InteractiveCard,
  WaveDivider,
  CurvedDivider,
  AngledDivider
};
